// File: app/src/components/workout/EffortMeter.js
//
// Perceived effort (RPE 1-10) as one bar of ten segments: easy on the left,
// max on the right, filled up to the pick. Replaces the row of small circles
// with tall, easy-to-hit targets that read as a scale at a glance. Shared by
// the Stop dialog, the end-of-workout screen and Log workout.

import React from "react";
import { RPE_HEX, RPE_LABELS } from "../../config/rpe";

const SCALE = Array.from({ length: 10 }, (_, i) => i + 1);

/**
 * @param {{ value: number|null, onChange: (n: number|null) => void,
 *   label?: React.ReactNode, compact?: boolean }} props
 * Tapping the picked segment again clears it.
 */
export default function EffortMeter({ value, onChange, label = null, compact = false }) {
  const picked = value >= 1 && value <= 10 ? value : null;
  return (
    <div className="w-full">
      <div className="mb-2 flex items-baseline justify-between gap-2 text-sm font-medium text-muted-foreground">
        <span className="flex items-center gap-1">{label}</span>
        {picked && (
          <span className="truncate font-semibold" style={{ color: RPE_HEX[picked] }}>
            {picked} · {RPE_LABELS[picked]}
          </span>
        )}
      </div>
      <div className="grid grid-cols-10 gap-[3px]" role="radiogroup" aria-label="Perceived effort">
        {SCALE.map((n) => {
          const filled = picked != null && n <= picked;
          const isPick = n === picked;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={isPick}
              aria-label={`${n}, ${RPE_LABELS[n]}`}
              title={RPE_LABELS[n]}
              onClick={() => onChange(isPick ? null : n)}
              className={`flex items-center justify-center rounded-md text-xs font-bold tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring first:rounded-l-[10px] last:rounded-r-[10px] ${
                compact ? "h-9" : "h-11"
              } ${filled ? "text-white" : "text-white/55 hover:text-white"}`}
              style={{
                background: filled ? RPE_HEX[n] : `${RPE_HEX[n]}26`,
                boxShadow: isPick ? "inset 0 0 0 2px #fff" : undefined,
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>Easy</span>
        <span>Max</span>
      </div>
    </div>
  );
}
